<template>
  <div class="photo-gallery">
    <FileUploadModal v-if="photoUploading" :progress="photoUploadProgress" title="Uploading photos..." />

    <!-- Photo upload modal -->
    <PhotoUploadModal
      :show="showUploadModal"
      :show-client-visibility-toggle="isFullManagement && !isClientUser"
      @close="showUploadModal = false"
      @upload="handlePhotoUpload"
    />

    <!-- Gallery header with title and actions -->
    <div class="gallery-header">
      <div class="gallery-title-section">
        <h2 class="gallery-title">{{ $t('projects.photoGallery') }}</h2>
        <p class="gallery-subtitle" v-if="photos.length > 0">
          {{ $t('projects.photoCount', { count: photos.length }) }}
        </p>
      </div>
      <div class="gallery-actions">
        <PermissionGuard :permission="'UPLOAD_PHOTO'">
          <button class="btn-modern primary" @click="handleUploadClick">
            <i class="pi pi-cloud-upload"></i>
            {{ $t('projects.uploadPhotos') }}
          </button>
        </PermissionGuard>
      </div>
    </div>
    
    <!-- Gallery Statistics -->
    <div class="stats-section" v-if="photos.length > 0">
      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon photos">
            <i class="pi pi-images"></i>
          </div>
        </div>
        <div class="stat-number">{{ photos.length }}</div>
        <div class="stat-label">{{ $t('projects.totalPhotos') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon storage">
            <i class="pi pi-database"></i>
          </div>
        </div>
        <div class="stat-number">{{ totalStorageSize }}</div>
        <div class="stat-label">{{ $t('projects.storageUsed') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon recent">
            <i class="pi pi-clock"></i>
          </div>
        </div>
        <div class="stat-number">{{ recentPhotosCount }}</div>
        <div class="stat-label">{{ $t('projects.recentPhotos') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">
          <div class="stat-icon camera">
            <i class="pi pi-camera"></i>
          </div>
        </div>
        <div class="stat-number">{{ videoCount }}</div>
        <div class="stat-label">{{ $t('projects.videos') }}</div>
      </div>
    </div>
    
    <!-- Controls Section -->
    <div class="controls-section" v-if="photos.length > 0">
      <div class="controls-row">
        <div class="search-bar">
          <i class="pi pi-search search-icon"></i>
          <input 
            type="text" 
            class="search-input" 
            v-model="searchQuery" 
            :placeholder="$t('projects.searchPhotos')" 
          />
        </div>
        <div class="view-controls">
          <div class="view-toggle">
            <button 
              :class="{ active: viewMode === 'grid' }" 
              @click="viewMode = 'grid'"
              :title="$t('projects.gridView')"
            >
              <i class="pi pi-th-large"></i>
              {{ $t('projects.grid') }}
            </button>
            <button 
              :class="{ active: viewMode === 'masonry' }" 
              @click="viewMode = 'masonry'"
              :title="$t('projects.masonryView')"
            >
              <i class="pi pi-table"></i>
              {{ $t('projects.masonry') }}
            </button>
            <button 
              :class="{ active: viewMode === 'list' }" 
              @click="viewMode = 'list'"
              :title="$t('projects.listView')"
            >
              <i class="pi pi-list"></i>
              {{ $t('projects.list') }}
            </button>
          </div>
          <select class="sort-dropdown" v-model="sortBy">
            <option value="newest">{{ $t('projects.sortNewest') }}</option>
            <option value="oldest">{{ $t('projects.sortOldest') }}</option>
            <option value="name-asc">{{ $t('projects.sortNameAsc') }}</option>
            <option value="name-desc">{{ $t('projects.sortNameDesc') }}</option>
          </select>
        </div>
      </div>
      <div class="filter-section">
        <div class="filter-tags">
          <span class="filter-label">{{ $t('projects.filterBy') }}:</span>
          <button 
            class="filter-tag" 
            :class="{ active: mediaFilter === 'all' }" 
            @click="mediaFilter = 'all'"
          >
            <span>{{ $t('projects.all') }}</span>
            <span class="tag-count">{{ photos.length }}</span>
          </button>
          <button 
            class="filter-tag" 
            :class="{ active: mediaFilter === 'images' }" 
            @click="mediaFilter = 'images'"
          >
            <span>{{ $t('projects.images') }}</span>
            <span class="tag-count">{{ imageCount }}</span>
          </button>
          <button 
            class="filter-tag" 
            :class="{ active: mediaFilter === 'videos' }" 
            @click="mediaFilter = 'videos'"
          >
            <span>{{ $t('projects.videos') }}</span>
            <span class="tag-count">{{ videoCount }}</span>
          </button>
        </div>
        
        <!-- Pagination Controls (same as bottom) -->
        <div v-if="totalPages > 1" class="pagination-controls pagination-top">
          <div class="pagination-info">
            {{ $t('common.pagination.showing') }} {{ paginationInfo.start }}-{{ paginationInfo.end }} {{ $t('common.of') }} {{ paginationInfo.total }}
          </div>
          <div class="pagination-buttons">
            <button 
              class="pagination-btn" 
              :disabled="currentPage === 1"
              @click="prevPage"
              :title="$t('common.previous')"
            >
              <i class="pi pi-chevron-left"></i>
            </button>
            
            <button 
              v-for="page in totalPages" 
              :key="page"
              v-show="page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2"
              class="pagination-btn page-number" 
              :class="{ active: currentPage === page }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
            
            <button 
              class="pagination-btn" 
              :disabled="currentPage === totalPages"
              @click="nextPage"
              :title="$t('common.next')"
            >
              <i class="pi pi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>    
    <!-- Loading state with skeleton -->
    <div v-if="loading" class="gallery-loading">
      <div class="skeleton-container">
        <div class="skeleton-item" v-for="n in 6" :key="n">
          <div class="skeleton-image"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-text short"></div>
        </div>
      </div>
    </div>
    
    <!-- Empty state when no photos are available -->
    <div v-else-if="photos.length === 0" class="gallery-empty">
      <div class="empty-illustration">
        <div class="empty-circle">
          <i class="pi pi-camera"></i>
        </div>
        <div class="empty-circles">
          <div class="circle circle-1"></div>
          <div class="circle circle-2"></div>
          <div class="circle circle-3"></div>
        </div>
      </div>
      <h3 class="empty-title">{{ $t('projects.noPhotosAvailable') }}</h3>
      <p class="empty-description">{{ $t('projects.noPhotosAvailableDescription') }}</p>
      <button class="btn-modern primary" @click="handleUploadClick">
        <i class="pi pi-plus"></i>
        {{ $t('projects.addFirstPhoto') }}
      </button>
    </div>
    
    <!-- Modern Galleria display -->    
    <div v-else class="gallery-container" :class="viewMode">
      <!-- Grid/Masonry/List View -->
      <div class="photo-grid" :class="viewMode">
        <div 
          v-for="photo in paginatedPhotos" 
          :key="photo.id" 
          class="photo-card"
          :class="{ 'is-video': photo.isVideo }"
          @click="photo.isVideo ? playVideo($event, photo) : zoomPhoto($event, photo)"
        >
          <div class="photo-card-image">
            <img 
              v-if="!photo.isVideo"
              :src="photo.secureUrl || PLACEHOLDER_SVG" 
              :alt="photo.fileName || 'Photo'" 
              class="card-image zoom-image"
              :class="{ 'loading': !photo.loaded }"
              @load="onImageLoad(photo)"
              @error="onImageError(photo)"
            />
            <video
              v-else
              :src="photo.secureUrl || ''"
              class="card-video"
              :class="{ 'loading': !photo.loaded }"
              muted
              playsinline
              preload="metadata"
              @loadeddata="onVideoLoad(photo)"
              @error="onVideoError(photo)"
            ></video>
            <div v-if="photo.isVideo" class="video-badge">
              <i class="pi pi-play"></i>
            </div>
            <div v-if="!photo.loaded" class="card-loading">
              <div class="loading-pulse">
                <div class="pulse-ring"></div>
                <div class="pulse-ring"></div>
                <div class="pulse-ring"></div>
              </div>
            </div>
            <div class="photo-overlay"></div>
            <div class="photo-card-actions">
              <button class="action-btn download" @click.stop="downloadPhoto(photo)" 
                      :title="$t('common.download')">
                <i class="pi pi-download"></i>
              </button>
              <PermissionGuard :permission="'UPLOAD_PHOTO'">
                <button v-if="!photo.isVideo" class="action-btn annotate" @click.stop="annotatePhoto(photo)" 
                        :title="$t('common.annotate')">
                  <i class="pi pi-pencil"></i>
                </button>
              </PermissionGuard>
              <PermissionGuard :permission="'DELETE_PHOTO'">
                <button v-if="canDeletePhoto(photo)" class="action-btn delete" @click.stop="deletePhoto(photo)" 
                        :title="$t('common.delete')">
                  <i class="pi pi-trash"></i>
                </button>
              </PermissionGuard>
            </div>
          </div>
          <div class="photo-card-info">
            <h4 class="photo-card-title">{{ photo.fileName || photo.originalName || 'Photo' }}</h4>
            <p class="photo-card-date">
              <i class="pi pi-calendar"></i>
              {{ formatDate(photo.createdAt) }}
            </p>
          </div>
        </div>
      </div>
      
      <!-- Pagination Controls -->
      <div v-if="viewMode !== 'gallery' && totalPages > 1" class="pagination-controls">
        <div class="pagination-info">
          {{ $t('common.pagination.showing') }} {{ paginationInfo.start }}-{{ paginationInfo.end }} {{ $t('common.of') }} {{ paginationInfo.total }}
        </div>
        <div class="pagination-buttons">
          <button 
            class="pagination-btn" 
            :disabled="currentPage === 1"
            @click="prevPage"
            :title="$t('common.previous')"
          >
            <i class="pi pi-chevron-left"></i>
          </button>
          
          <button 
            v-for="page in totalPages" 
            :key="page"
            v-show="page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2"
            class="pagination-btn page-number" 
            :class="{ active: currentPage === page }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          
          <button 
            class="pagination-btn" 
            :disabled="currentPage === totalPages"
            @click="nextPage"
            :title="$t('common.next')"
          >
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Photo Annotator Modal -->
  <teleport to="body">
    <DocumentAnnotator
      v-if="showPhotoAnnotator && selectedPhotoForAnnotation && photoAnnotatorUrl && projectId"
      :projectId="Number(projectId)"
      :documentId="Number(selectedPhotoForAnnotation?.id)"
      :documentUrl="photoAnnotatorUrl"
      :fileName="selectedPhotoForAnnotation?.fileName || selectedPhotoForAnnotation?.originalName || selectedPhotoForAnnotation?.name || 'Photo'"
      :fileType="getFileType(selectedPhotoForAnnotation)"
      @close="closePhotoAnnotator"
      @saved="handleAnnotationSaved"
    />
  </teleport>

  <!-- Video Player Modal -->
  <teleport to="body">
    <div v-if="showVideoModal" class="video-modal-overlay" @click="closeVideoModal">
      <div class="video-modal-container" @click.stop>
        <button class="video-modal-close" @click="closeVideoModal" :title="$t('common.close')">
          <i class="pi pi-times"></i>
        </button>
        <div class="video-modal-content">
          <video
            v-if="selectedVideo"
            :src="selectedVideo.secureUrl || ''"
            class="video-modal-player"
            controls
            autoplay
            playsinline
          ></video>
          <div v-if="selectedVideo" class="video-modal-info">
            <h3 class="video-modal-title">{{ selectedVideo.fileName || selectedVideo.originalName || 'Video' }}</h3>
            <p class="video-modal-date">{{ formatDate(selectedVideo.createdAt) }}</p>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
// @ts-ignore
import { ref, onMounted, onUnmounted, watch, reactive, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import { useStore } from '@/composables/useStore';
import { useAuthStore, useProjectStore } from '@/stores';
import ProjectService from '../../../services/project.service';
import FileUploadModal from '@/components/project/documents/FileUploadModal.vue';
import PhotoUploadModal from './PhotoUploadModal.vue';
import DocumentAnnotator from '@/components/project/documents/DocumentAnnotator.vue';
import { fetchImageSecurely, isUrlSafe } from '../../../utils/urlUtils'; // Added import
// import { lruImageCache } from '../../../utils/urlUtils'; // Temporarily commented out due to missing export
import { sanitizeFileNames } from '@/utils/fileNameSanitizer';
import mediumZoom from 'medium-zoom';
import PermissionGuard from '@/components/common/PermissionGuard.vue';

interface Props {
  projectId: string | number;
}

export default {
  name: 'PhotoGallery',
  components: {
    PermissionGuard,
    FileUploadModal,
    PhotoUploadModal,
    DocumentAnnotator
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  setup(props: Props) {
    const { t } = useI18n();
    const { notifyError, notifyConfirm, notifySuccess } = useNotifications();
    const store = useStore();
    const authStore = useAuthStore();
    const projectStore = useProjectStore();
    const galleria = ref(null);
    const loading = ref(true);
    const photoUploading = ref(false);
    const photoUploadProgress = ref(0);
    const photos = ref([]);
    const activeIndex = ref(0);
    const zoom = ref(null);
    const imageCache = reactive(new Map<string, string>());
    const PLACEHOLDER_SVG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNmOWZhZmIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgYWxpZ25tZW50LWJhc2VsaW5lPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJtb25vc3BhY2UsIHNhbnMtc2VyaWYiIGZpbGw9IiNBQUEiPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+'; // Added constant

    // Detect project management type
    const isFullManagement = computed(() =>
      projectStore.currentProject?.managementType === 'FULL_MANAGEMENT'
    )

    const isClientUser = computed(() => authStore.user?.type === 'CLIENT')

    // Upload modal visibility
    const showUploadModal = ref(false);

    // Photo annotation state
    const showPhotoAnnotator = ref(false);
    const selectedPhotoForAnnotation = ref(null);
    const photoAnnotatorUrl = ref<string | null>(null);
    
    // Video modal state
    const showVideoModal = ref(false);
    const selectedVideo = ref(null);
    
    // New gallery controls
    const viewMode = ref('grid'); // grid, masonry, list
    const searchQuery = ref('');
    const sortBy = ref('newest');
    const mediaFilter = ref('all'); // all, images, videos
    
    // Pagination
    const currentPage = ref(1);
    const itemsPerPage = ref(8);

    // Load photos from API
    onMounted(() => {
      loadProjectPhotos();

      // Initialize medium-zoom after a short delay to ensure DOM is ready
      setTimeout(() => {
        initializeZoom();
      }, 500);
    });

    // Clean up on unmount
    onUnmounted(() => {
      if (zoom.value) {
        zoom.value.detach();
      }
      // Clean up all blob URLs from cache
      for (const blobUrl of imageCache.values()) {
        if (blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      }
      imageCache.clear();

      // Clean up blob URLs from photos array
      photos.value.forEach((photo: any) => {
        if (photo.secureUrl && typeof photo.secureUrl === 'string' && photo.secureUrl.startsWith('blob:')) {
          URL.revokeObjectURL(photo.secureUrl);
        }
      });
    });    // Initialize medium-zoom on the current image
    const initializeZoom = (event?: any) => {
      // If zoom already exists, detach it first
      if (zoom.value) {
        zoom.value.detach();
      }

      // Create new medium-zoom instance with improved options
      zoom.value = mediumZoom('.zoom-image', {
        margin: 40,
        background: 'rgba(0, 0, 0, 0.9)',
        scrollOffset: 0
      });
      
      // If this was triggered by a click, open the zoom immediately
      if (event && event.type === 'click') {
        const clickedImage = event.currentTarget.querySelector('.zoom-image');
        if (clickedImage) {
          setTimeout(() => {
            zoom.value.open({ target: clickedImage });
          }, 50);
        }
      }

      // Add zoom-in effect on hover
      zoom.value.on('open', () => {
        document.body.style.overflow = 'hidden';
      });

      zoom.value.on('close', () => {
        document.body.style.overflow = '';
      });
    };    // Method to fetch photos from API

    const fetchSecureImage = async (photoObject: any): Promise<boolean> => {
      if (!photoObject || !photoObject.id) {
        console.error('fetchSecureImage: Invalid photo object or missing ID', photoObject);
        if (photoObject) photoObject.secureUrl = PLACEHOLDER_SVG;
        return false;
      }

      const photoId = photoObject.id;

      if (imageCache.has(photoId)) {
        const cachedUrl = imageCache.get(photoId);
        if (cachedUrl) { // Ensure cachedUrl is not null/undefined before assigning
            photoObject.secureUrl = cachedUrl;
            photoObject.loaded = true;
            return true;
        }
      }

      try {
        const blobUrl = await fetchImageSecurely(String(props.projectId), photoId);
        if (blobUrl) { // Ensure blobUrl is not null/undefined
            imageCache.set(photoId, blobUrl);
            photoObject.secureUrl = blobUrl;
            photoObject.loaded = true;
            return true;
        } else {
            photoObject.secureUrl = PLACEHOLDER_SVG; // Fallback if blobUrl is null
            photoObject.loaded = true;
            return false;
        }
      } catch (error) {
        console.error(`Failed to fetch secure image for photoId ${photoId}:`, error);
        photoObject.secureUrl = PLACEHOLDER_SVG;
        photoObject.loaded = true;
        return false;
      }
    };

    // Computed properties for filtering and statistics (moved up before loadProjectPhotos)
    const filteredAndSortedPhotos = computed(() => {
      let filtered = [...photos.value];
      
      // Apply media filter
      if (mediaFilter.value === 'images') {
        filtered = filtered.filter((p: any) => !p.isVideo);
      } else if (mediaFilter.value === 'videos') {
        filtered = filtered.filter((p: any) => p.isVideo);
      }
      
      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter((p: any) => {
          const fileName = (p.fileName || p.originalName || p.name || '').toLowerCase();
          return fileName.includes(query);
        });
      }
      
      // Apply sorting
      if (sortBy.value === 'newest') {
        filtered.sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });
      } else if (sortBy.value === 'oldest') {
        filtered.sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateA - dateB;
        });
      } else if (sortBy.value === 'name-asc') {
        filtered.sort((a: any, b: any) => {
          const nameA = (a.fileName || a.originalName || a.name || '').toLowerCase();
          const nameB = (b.fileName || b.originalName || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } else if (sortBy.value === 'name-desc') {
        filtered.sort((a: any, b: any) => {
          const nameA = (a.fileName || a.originalName || a.name || '').toLowerCase();
          const nameB = (b.fileName || b.originalName || b.name || '').toLowerCase();
          return nameB.localeCompare(nameA);
        });
      }
      
      return filtered;
    });
    
    // Pagination computed properties (moved up)
    const totalPages = computed(() => {
      return Math.ceil(filteredAndSortedPhotos.value.length / itemsPerPage.value);
    });
    
    const paginatedPhotos = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return filteredAndSortedPhotos.value.slice(start, end);
    });
    
    const paginationInfo = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value + 1;
      const end = Math.min(currentPage.value * itemsPerPage.value, filteredAndSortedPhotos.value.length);
      const total = filteredAndSortedPhotos.value.length;
      return { start, end, total };
    });

    const loadProjectPhotos = async () => {
      try {
        loading.value = true;
        const response = await ProjectService.getProjectPhotos(String(props.projectId));
        const photosData = response?.data || response;

        if (!Array.isArray(photosData)) {
            console.error('Error loading photos: photosData is not an array.', photosData);
            photos.value = [];
            loading.value = false;
            return;
        }

        // Clear previous image cache and revoke old blob URLs
        for (const blobUrl of imageCache.values()) {
          if (blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrl);
          }
        }
        imageCache.clear();

        // Revoke blob URLs from current photos.value before replacing it
        photos.value.forEach((photo: any) => {
            if (photo.secureUrl && typeof photo.secureUrl === 'string' && photo.secureUrl.startsWith('blob:')) {
                URL.revokeObjectURL(photo.secureUrl);
            }
        });
        
        // Initialize photos with placeholder media, but don't fetch secure URLs yet
        photos.value = photosData.map(apiPhoto => {
          // backend sometimes uses `fileType` instead of `type` (see ProjectPhotoResponse.fileType)
          const mimeType = (apiPhoto.type || apiPhoto.fileType || apiPhoto.file_type || apiPhoto.mimeType || apiPhoto.mime_type || '')?.toString();
          return reactive({
            ...apiPhoto,
            secureUrl: PLACEHOLDER_SVG, // Initial placeholder, will be updated when needed
            loaded: false, // Add a flag to track if the media has been loaded
            loading: false, // Add a flag to prevent duplicate fetch requests
            mimeType,
            isVideo: mimeType.startsWith('video/')
          });
        });
        
        // Initial fetch for visible photos on first page
        await nextTick();
        for (const photo of paginatedPhotos.value) {
          if (!photo.loaded) {
            await fetchSecureImage(photo);
          }
        }
        
        setTimeout(() => {
          if (galleria.value && galleria.value.$el) {
            galleria.value.$el.focus();
          }
        }, 200);
      } catch (error) {
        console.error('Error loading photos:', error);
        photos.value = [];
      } finally {
        loading.value = false;
      }
    };
      // Handle upload button click — open the upload modal
    const handleUploadClick = () => {
      showUploadModal.value = true;
    };

    // Image loading handlers
    const onImageLoad = (photo: any) => {
      photo.loaded = true;
    };

    const onVideoLoad = (photo: any) => {
      // fired when enough data is available to play
      photo.loaded = true;
    };

    const onImageError = (photo: any) => {
      console.error('Failed to load image:', photo.id, 'secureUrl=', photo.secureUrl, 'api url=', photo.url);
      // If server provides a public url (e.g., external CDN) and it's safe, try using it as a fallback
      try {
        // Only fall back to absolute public URLs (http/https). Avoid falling back to
        // relative API endpoints (like `/api/project/...`) because those require
        // Authorization headers and the browser <img>/<video> tag won't send them.
        if (photo.url && typeof photo.url === 'string' && /^https?:\/\//i.test(photo.url) && photo.secureUrl !== photo.url) {
          console.info('Falling back to public URL for photo', photo.id, photo.url);
          photo.secureUrl = photo.url;
          return; // allow the <img> to retry loading
        }
      } catch (e) {
        console.warn('Error checking fallback URL safety', e);
      }

      photo.secureUrl = PLACEHOLDER_SVG;
      photo.loaded = true;
    };

    const onVideoError = (photo: any) => {
      console.error('Failed to load video:', photo.id, 'secureUrl=', photo.secureUrl, 'api url=', photo.url);
      try {
        // Same as images: only fall back to absolute http(s) public URLs. Do NOT use
        // relative API endpoints because they require Authorization headers.
        if (photo.url && typeof photo.url === 'string' && /^https?:\/\//i.test(photo.url) && photo.secureUrl !== photo.url) {
          console.info('Falling back to public URL for video', photo.id, photo.url);
          photo.secureUrl = photo.url;
          return;
        }
      } catch (e) {
        console.warn('Error checking fallback URL safety', e);
      }

      photo.secureUrl = PLACEHOLDER_SVG;
      photo.loaded = true;
    };

    // Process files — called from PhotoUploadModal upload event
    const processFiles = async (files: File[], sharedWithClient: boolean) => {
      const galleriaElement = galleria.value && (galleria.value as any).$el;
      const scrollPosition = galleriaElement ? galleriaElement.scrollTop : 0;
      
      try {
        loading.value = true;
        
        // Sanitize file names before uploading
        const sanitizedFiles = sanitizeFileNames(files);
        
        const formData = new FormData();
        sanitizedFiles.forEach(file => {
          formData.append('photos', file);
        });
        formData.append('sharedWithClient', String(sharedWithClient));
        photoUploading.value = true;
        photoUploadProgress.value = 0;
        await ProjectService.uploadProjectPhotos(String(props.projectId), formData, (progressEvent) => {
          const loaded = (progressEvent as any)?.loaded ?? 0;
          const total = (progressEvent as any)?.total ?? 0;
          if (total > 0) {
            photoUploadProgress.value = Math.round((loaded / total) * 100);
          }
        });
        photoUploadProgress.value = 100;
        
        await loadProjectPhotos(); // Reloads all photos, including new ones with secure URLs
        
        if (galleriaElement) {
            setTimeout(() => { galleriaElement.scrollTop = scrollPosition; }, 100);
        }
        
        setTimeout(() => {
          initializeZoom();
        }, 300);
      } catch (error) {
        console.error('Failed to upload photos:', error);
        notifyError(t('errors.photoUploadFailed'));
      } finally {
        loading.value = false;
        photoUploading.value = false;
      }
    };

    // Handle upload event from PhotoUploadModal
    const handlePhotoUpload = async ({ files, sharedWithClient }: { files: File[]; sharedWithClient: boolean }) => {
      showUploadModal.value = false;
      await processFiles(files, sharedWithClient);
    };

    // Download photo
    const downloadPhoto = async (photo: any) => {
      try {
        loading.value = true;
        const downloadFileName = photo.fileName || photo.name || `photo-${photo.id || 'local'}.jpg`;

        if (photo.secureUrl && typeof photo.secureUrl === 'string' && photo.secureUrl.startsWith('blob:')) {
          const a = document.createElement('a');
          a.href = photo.secureUrl;
          a.download = downloadFileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }

        if (photo.id && !photo.isLocal) {
          const photoId = photo.id;
          const response = await fetch(`/api/project/${props.projectId}/photos/${photoId}`, {
            headers: { 'Authorization': `Bearer ${store.getters.getAccessToken}` },
            credentials: 'same-origin'
          });
          if (!response.ok) {
            throw new Error(`Failed to download image ${photoId}: ${response.status} ${response.statusText}`);
          }
          const blob = await response.blob();
          const temporaryBlobUrl = URL.createObjectURL(blob);
          
          const a = document.createElement('a');
          a.href = temporaryBlobUrl;
          a.download = downloadFileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(temporaryBlobUrl);
        } else if (photo.isLocal) {
          if (photo.file instanceof File) {
            const temporaryBlobUrl = URL.createObjectURL(photo.file);
            const a = document.createElement('a');
            a.href = temporaryBlobUrl;
            a.download = downloadFileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(temporaryBlobUrl);
          } else {
             throw new Error('Cannot download local photo: secureUrl is not a blob and no file object available.');
          }
        } else {
          throw new Error('Cannot download media: No ID for server media or invalid local media state.');
        }
      } catch (error) {
        console.error('Error downloading photo:', error);
        notifyError(t('errors.photoDownloadFailed'));
      } finally {
        loading.value = false;
      }
    };
    
    // Annotate photo
    const annotatePhoto = async (photo: any) => {
      if (loading.value || !props.projectId || !photo?.id) return;

      try {
        loading.value = true;
        
        // Get photo data for annotation
        const result = await fetchImageSecurely(String(props.projectId), photo.id);
        
        if (result) {
          // Clean up previous URL if exists
          if (photoAnnotatorUrl.value) {
            URL.revokeObjectURL(photoAnnotatorUrl.value);
          }
          
          // Use the blob URL for annotation
          photoAnnotatorUrl.value = result;
          selectedPhotoForAnnotation.value = photo;
          showPhotoAnnotator.value = true;
        } else {
          console.error('Invalid response format - expected a blob URL');
          notifyError(t('errors.invalidResponseFormat'));
        }
      } catch (err: any) {
        console.error('Error loading photo for annotation:', err);
        let errorMessage = t('errors.loadingPhotoError');
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        notifyError(errorMessage);
      } finally {
        loading.value = false;
      }
    };
    
    // Delete photo
    const deletePhoto = async (photo: any) => {
      notifyConfirm(
        t('projects.confirmDeletePhoto'),
        async () => {
          try {
            loading.value = true;

            // If it's a server photo, delete it via API
            if (photo.id && !photo.isLocal) {
                await ProjectService.deleteProjectPhoto(String(props.projectId), photo.id);
            }

            // Clean up blob URL from cache and the photo object itself
            if (photo.secureUrl && typeof photo.secureUrl === 'string' && photo.secureUrl.startsWith('blob:')) {
              URL.revokeObjectURL(photo.secureUrl);
            }
            if (photo.id && imageCache.has(photo.id)) {
              const cachedBlobUrl = imageCache.get(photo.id);
              if (cachedBlobUrl && typeof cachedBlobUrl === 'string' && cachedBlobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(cachedBlobUrl);
              }
              imageCache.delete(photo.id);
            }
            
            // Remove from the photos array
            const photoIndex = photos.value.findIndex((p: any) => p.id === photo.id || (p.isLocal && p.file === photo.file));
            if (photoIndex !== -1) {
              photos.value.splice(photoIndex, 1);
            }

            // Update active index if necessary
            const currentIndex = activeIndex.value;
            if (photos.value.length > 0) {
              if (currentIndex >= photos.value.length) {
                activeIndex.value = photos.value.length - 1;
              } else {
                activeIndex.value = currentIndex;
              }
            }

            // Force Galleria component to update its view
            nextTick(() => {
              if (galleria.value) {
                galleria.value.$forceUpdate();
                initializeZoom();
              }
            });
            
            notifySuccess(t('projects.photoDeletedSuccess'));
          } catch (error) {
            console.error('Failed to delete photo:', error);
            notifyError(t('projects.errorDeletingPhoto'));
          } finally {
            loading.value = false;
          }
        }
      );
    };

    // Check whether current user is the uploader/owner of the photo
    const canDeletePhoto = (photo: any) => {
      try {
        const user = store.getters.getUser.value;
        if (!user || !photo) return false;
        const uid = String(user.id || '');
        // Only allow delete when the creator (createdBy) matches the current user
        return photo?.createdBy && String(photo.createdBy) === uid;
      } catch (e) {
        return false;
      }
    };
    
    // Format date string to a readable format
    const formatDate = (dateString: string | null | undefined) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });    };

    // Close photo annotator
    const closePhotoAnnotator = () => {
      showPhotoAnnotator.value = false;
      if (photoAnnotatorUrl.value) {
        URL.revokeObjectURL(photoAnnotatorUrl.value);
        photoAnnotatorUrl.value = null;
      }
      selectedPhotoForAnnotation.value = null;
    };

    // Handle annotation saved
    const handleAnnotationSaved = () => {
      // Refresh the photo to show updated annotations
      // For now, just close the annotator
      closePhotoAnnotator();
      notifySuccess(t('annotation.saved'));
    };

    // Get file type from photo object
    const getFileType = (photo: any) => {
      if (!photo) return 'jpg';
      
      // Try different possible file type fields
      const mimeType = (photo.type || photo.fileType || photo.file_type || photo.mimeType || photo.mime_type || '')?.toString();
      if (mimeType) {
        // Extract file extension from mime type
        if (mimeType.startsWith('image/')) {
          return mimeType.split('/')[1];
        }
      }
      
      // Fallback to file extension from filename
      const fileName = photo.fileName || photo.originalName || photo.name || '';
      if (fileName) {
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (extension) return extension;
      }
      
      return 'jpg'; // Default fallback
    };
    
    // Zoom photo using medium-zoom
    const zoomPhoto = (event: Event, photo: any) => {
      if (!photo.loaded || photo.isVideo) return;
      
      const target = event.currentTarget as HTMLElement;
      const img = target.querySelector('.card-image') as HTMLImageElement;
      
      if (img) {
        // Initialize zoom if not already done
        if (!zoom.value) {
          zoom.value = mediumZoom('.zoom-image', {
            margin: 40,
            background: 'rgba(0, 0, 0, 0.9)',
            scrollOffset: 0
          });
          
          zoom.value.on('open', () => {
            document.body.style.overflow = 'hidden';
          });

          zoom.value.on('close', () => {
            document.body.style.overflow = '';
          });
        }
        
        // Attach this image to zoom if not already attached
        zoom.value.attach(img);
        
        // Open zoom
        setTimeout(() => {
          zoom.value.open({ target: img });
        }, 10);
      }
    };
    
    // Play video in modal
    const playVideo = (_event: Event, photo: any) => {
      if (!photo.loaded || !photo.isVideo) return;
      
      selectedVideo.value = photo;
      showVideoModal.value = true;
    };
    
    // Close video modal
    const closeVideoModal = () => {
      showVideoModal.value = false;
      selectedVideo.value = null;
    };
    
    const imageCount = computed(() => {
      return photos.value.filter((p: any) => !p.isVideo).length;
    });
    
    const videoCount = computed(() => {
      return photos.value.filter((p: any) => p.isVideo).length;
    });
    
    const totalStorageSize = computed(() => {
      const totalBytes = photos.value.reduce((sum: number, p: any) => {
        return sum + (p.fileSize || 0);
      }, 0);
      
      if (totalBytes < 1024) return `${totalBytes} B`;
      if (totalBytes < 1024 * 1024) return `${(totalBytes / 1024).toFixed(1)} KB`;
      if (totalBytes < 1024 * 1024 * 1024) return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
      return `${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    });
    
    const recentPhotosCount = computed(() => {
      const threeDayAgo = new Date();
      threeDayAgo.setDate(threeDayAgo.getDate() - 3);
      return photos.value.filter((p: any) => {
        const createdDate = new Date(p.createdAt || 0);
        return createdDate > threeDayAgo;
      }).length;
    });
    
    // Pagination methods
    const goToPage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        // Scroll to top of gallery
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        goToPage(currentPage.value + 1);
      }
    };
    
    const prevPage = () => {
      if (currentPage.value > 1) {
        goToPage(currentPage.value - 1);
      }
    };
    
    // Reset to first page when filters change
    watch([searchQuery, sortBy, mediaFilter], () => {
      currentPage.value = 1;
    });
    
    // Fetch images when page changes - this is the primary loader
    watch(currentPage, async () => {
      if (viewMode.value !== 'gallery') {
        // Load images for the new page
        await nextTick();
        // Load all images in parallel instead of sequentially
        const promises = paginatedPhotos.value
          .filter((photo: any) => !photo.loaded && !photo.loading)
          .map(async (photo: any) => {
            photo.loading = true; // Prevent duplicate fetches
            await fetchSecureImage(photo);
            photo.loading = false;
          });
        await Promise.all(promises);
      }
    });
    
    // Removed duplicate paginatedPhotos watcher - it was causing photos to load multiple times
    // The currentPage watcher above handles all pagination loading
    
    // Fetch images when active index changes in gallery mode
    watch(activeIndex, async (newIndex: number) => {
      if (viewMode.value === 'gallery') {
        // Load current photo and a few around it in parallel
        const filtered = filteredAndSortedPhotos.value;
        const startIdx = Math.max(0, newIndex - 2);
        const endIdx = Math.min(filtered.length - 1, newIndex + 2);
        
        const promises = [];
        for (let i = startIdx; i <= endIdx; i++) {
          const photo = filtered[i];
          if (photo && !photo.loaded && !photo.loading) {
            photo.loading = true; // Prevent duplicate fetches
            promises.push(
              fetchSecureImage(photo).then(() => {
                photo.loading = false;
              })
            );
          }
        }
        await Promise.all(promises);
      }
    });
    
    // When switching to gallery mode, load the current photo
    watch(viewMode, async (newMode: string) => {
      if (newMode === 'gallery') {
        const filtered = filteredAndSortedPhotos.value;
        const photo = filtered[activeIndex.value];
        if (photo && !photo.loaded && !photo.loading) {
          photo.loading = true; // Prevent duplicate fetches
          await fetchSecureImage(photo);
          photo.loading = false;
        }
      }
    });

    return {
      galleria,
      loading,
      photos,
      activeIndex,
      zoom,
      PLACEHOLDER_SVG,
      formatDate,
      handleUploadClick,
      handlePhotoUpload,
      showUploadModal,
      onImageLoad,
      onImageError,
      downloadPhoto,
      annotatePhoto,
      onVideoLoad,
      onVideoError,
      deletePhoto,
      canDeletePhoto,
      initializeZoom,
      closePhotoAnnotator,
      handleAnnotationSaved,
      getFileType,
      photoUploading,
      photoUploadProgress,
      showPhotoAnnotator,
      selectedPhotoForAnnotation,
      photoAnnotatorUrl,
      showVideoModal,
      selectedVideo,
      closeVideoModal,
      viewMode,
      searchQuery,
      sortBy,
      mediaFilter,
      filteredAndSortedPhotos,
      imageCount,
      videoCount,
      totalStorageSize,
      recentPhotosCount,
      zoomPhoto,
      playVideo,
      currentPage,
      itemsPerPage,
      totalPages,
      paginatedPhotos,
      paginationInfo,
      goToPage,
      nextPage,
      prevPage,
      isFullManagement,
      isClientUser,
    };
  }
};
</script>

<style scoped>
/* Modern Photo Gallery Styles */
.photo-gallery {
  width: 100%;
  min-height: 400px;
}

/* ===== HEADER SECTION ===== */
.gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;
}

.gallery-title-section {
  flex: 1;
}

.gallery-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: var(--surface-900, #1f2937);
  letter-spacing: -0.025em;
}

.gallery-subtitle {
  font-size: 0.875rem;
  color: var(--surface-600, #6b7280);
  margin: 0;
  font-weight: 500;
}

.gallery-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.btn-modern.primary {
  background: linear-gradient(135deg, var(--primary-500, #6366f1) 0%, var(--primary-600, #4f46e5) 100%);
  color: white;
  box-shadow: 0 4px 14px 0 rgba(99, 102, 241, 0.4);
}

.btn-modern.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(99, 102, 241, 0.5);
}

.btn-modern.secondary {
  background: white;
  color: var(--neutral-700, #374151);
  border: 2px solid var(--neutral-200, #e5e7eb);
}

.btn-modern.secondary:hover {
  border-color: var(--primary-500, #6366f1);
  color: var(--primary-600, #4f46e5);
  transform: translateY(-2px);
}

/* ===== STATS SECTION ===== */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid var(--neutral-200, #e5e7eb);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.stat-card:nth-child(1)::before { background: var(--primary-500, #6366f1); }
.stat-card:nth-child(2)::before { background: var(--green-500, #10b981); }
.stat-card:nth-child(3)::before { background: var(--orange-500, #f59e0b); }
.stat-card:nth-child(4)::before { background: var(--blue-500, #3b82f6); }

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.stat-icon.photos {
  background: rgba(99, 102, 241, 0.1);
  color: var(--primary-500, #6366f1);
}

.stat-icon.storage {
  background: rgba(16, 185, 129, 0.1);
  color: var(--green-500, #10b981);
}

.stat-icon.recent {
  background: rgba(245, 158, 11, 0.1);
  color: var(--orange-500, #f59e0b);
}

.stat-icon.camera {
  background: rgba(59, 130, 246, 0.1);
  color: var(--blue-500, #3b82f6);
}

.stat-number {
  font-size: 2rem;
  font-weight: 800;
  color: var(--surface-900, #1f2937);
  margin-bottom: 0.25rem;
}

.stat-label {
  color: var(--surface-600, #6b7280);
  font-size: 0.875rem;
  font-weight: 500;
}

/* ===== CONTROLS SECTION ===== */
.controls-section {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid var(--neutral-200, #e5e7eb);
}

.controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.search-bar {
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid var(--neutral-200, #e5e7eb);
  border-radius: 0.75rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: var(--neutral-50, #f9fafb);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500, #6366f1);
  background: white;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-400, #9ca3af);
}

.view-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.view-toggle {
  display: flex;
  background: var(--neutral-100, #f3f4f6);
  border-radius: 0.75rem;
  padding: 0.25rem;
  gap: 0.25rem;
}

.view-toggle button {
  background: transparent;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  color: var(--neutral-600, #6b7280);
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.view-toggle button.active {
  background: white;
  color: var(--primary-600, #4f46e5);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.sort-dropdown {
  padding: 0.75rem 1rem;
  border: 2px solid var(--neutral-200, #e5e7eb);
  border-radius: 0.75rem;
  background: white;
  color: var(--neutral-700, #374151);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
}

.sort-dropdown:hover {
  border-color: var(--primary-500, #6366f1);
}

.sort-dropdown:focus {
  outline: none;
  border-color: var(--primary-500, #6366f1);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.filter-section {
  padding-top: 1rem;
  border-top: 1px solid var(--neutral-200, #e5e7eb);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  flex: 1;
}

.filter-label {
  font-weight: 600;
  color: var(--neutral-700, #374151);
  font-size: 0.875rem;
}

.filter-tag {
  background: var(--neutral-100, #f3f4f6);
  border: 2px solid var(--neutral-200, #e5e7eb);
  color: var(--neutral-700, #374151);
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-tag:hover {
  border-color: var(--neutral-300, #d1d5db);
  background: var(--neutral-50, #f9fafb);
}

.filter-tag.active {
  background: var(--primary-500, #6366f1);
  color: white;
  border-color: var(--primary-500, #6366f1);
}

.tag-count {
  background: rgba(255, 255, 255, 0.3);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  min-width: 24px;
  text-align: center;
}

.filter-tag:not(.active) .tag-count {
  background: var(--neutral-200, #e5e7eb);
  color: var(--neutral-600, #6b7280);
}

/* ===== PAGINATION IN HEADER (TOP) ===== */
.pagination-top {
  margin-top: 0;
  padding: 0;
  background: transparent;
  box-shadow: none;
  border: none;
}

/* ===== UPLOAD ZONE ===== */
.upload-zone-container {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid var(--neutral-200, #e5e7eb);
  margin-bottom: 1.5rem;
}

.upload-zone {
  border: 3px dashed var(--neutral-300, #d1d5db);
  border-radius: 1.5rem;
  padding: 4rem 2rem;
  text-align: center;
  background: var(--neutral-50, #f9fafb);
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-zone.drag-over {
  border-color: var(--primary-500, #6366f1);
  background: rgba(99, 102, 241, 0.05);
  transform: scale(1.02);
}

.upload-zone-content {
  max-width: 500px;
  margin: 0 auto;
}

.upload-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, var(--primary-500, #6366f1) 0%, var(--primary-600, #4f46e5) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
}

.upload-text {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--surface-900, #1f2937);
  margin-bottom: 0.5rem;
}

.upload-subtext {
  color: var(--surface-600, #6b7280);
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
}

/* ===== PHOTO GRID VIEWS ===== */
.gallery-container {
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid var(--neutral-200, #e5e7eb);
}

.gallery-container.grid .photo-grid,
.gallery-container.masonry .photo-grid,
.gallery-container.list .photo-grid {
  padding: 1.5rem;
}

.photo-grid.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.photo-grid.masonry {
  column-count: 4;
  column-gap: 1.5rem;
}

.photo-grid.list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Photo Card */
.photo-card {
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  background: var(--neutral-50, #f9fafb);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  break-inside: avoid;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  border: 2px solid var(--neutral-100, #f3f4f6);
}

.photo-card:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-color: rgba(99, 102, 241, 0.3);
}

.photo-card-image {
  position: relative;
  width: 100%;
  height: 280px;
  overflow: hidden;
  background: var(--neutral-100, #f3f4f6);
}

.photo-grid.list .photo-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.photo-grid.list .photo-card-image {
  width: 200px;
  height: 150px;
  flex-shrink: 0;
  border-radius: 0.75rem;
}

.card-image,
.card-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.photo-card:hover .card-image,
.photo-card:hover .card-video {
  transform: scale(1.05);
}

.card-image.loading,
.card-video.loading {
  filter: blur(4px) brightness(0.8);
}

.video-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--primary-600, #4f46e5);
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.card-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.photo-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-card:hover .photo-overlay {
  opacity: 1;
}

.photo-card-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 3;
}

.photo-card:hover .photo-card-actions {
  opacity: 1;
  transform: translateY(0);
}

.action-btn {
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--surface-600, #6b7280);
  font-size: 0.875rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-btn:hover {
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-btn.download:hover {
  color: var(--blue-600, #2563eb);
  background: rgba(59, 130, 246, 0.1);
}

.action-btn.annotate:hover {
  color: var(--green-600, #16a34a);
  background: rgba(16, 185, 129, 0.1);
}

.action-btn.delete:hover {
  color: var(--red-600, #dc2626);
  background: rgba(220, 38, 38, 0.1);
}

.photo-card-info {
  padding: 1rem;
}

.photo-grid.list .photo-card-info {
  flex: 1;
  padding: 0;
}

.photo-card-title {
  font-weight: 700;
  font-size: 0.95rem;
  margin: 0 0 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--surface-900, #1f2937);
}

.photo-card-date {
  font-size: 0.875rem;
  color: var(--surface-600, #6b7280);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

/* ===== LOADING STATES ===== */
.gallery-loading {
  padding: 2rem 0;
}

.skeleton-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.skeleton-item {
  background: var(--surface-50, #f9fafb);
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-image {
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, var(--surface-200, #e5e7eb) 25%, var(--surface-100, #f3f4f6) 50%, var(--surface-200, #e5e7eb) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

.skeleton-text {
  height: 1rem;
  background: linear-gradient(90deg, var(--surface-200, #e5e7eb) 25%, var(--surface-100, #f3f4f6) 50%, var(--surface-200, #e5e7eb) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  margin: 1rem;
  border-radius: 0.5rem;
}

.skeleton-text.short {
  width: 60%;
  height: 0.75rem;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ===== EMPTY STATE ===== */
.gallery-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 24rem;
  text-align: center;
  background: linear-gradient(135deg, var(--surface-50, #f9fafb) 0%, var(--surface-100, #f3f4f6) 100%);
  border-radius: 1.5rem;
  padding: 3rem 2rem;
  border: 2px dashed var(--surface-300, #d1d5db);
  position: relative;
  overflow: hidden;
}

.empty-illustration {
  position: relative;
  margin-bottom: 2rem;
}

.empty-circle {
  width: 5rem;
  height: 5rem;
  background: linear-gradient(135deg, var(--primary-500, #3b82f6) 0%, var(--primary-600, #2563eb) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.5);
  position: relative;
  z-index: 2;
}

.empty-circles {
  position: absolute;
  inset: 0;
}

.circle {
  position: absolute;
  border: 2px solid var(--primary-200, #c7d2fe);
  border-radius: 50%;
  opacity: 0.6;
}

.circle-1 {
  width: 6rem;
  height: 6rem;
  top: -0.5rem;
  left: -0.5rem;
  animation: pulse-circle 2s ease-in-out infinite;
}

.circle-2 {
  width: 7rem;
  height: 7rem;
  top: -1rem;
  left: -1rem;
  animation: pulse-circle 2s ease-in-out infinite 0.3s;
}

.circle-3 {
  width: 8rem;
  height: 8rem;
  top: -1.5rem;
  left: -1.5rem;
  animation: pulse-circle 2s ease-in-out infinite 0.6s;
}

@keyframes pulse-circle {
  0%, 100% { 
    transform: scale(1);
    opacity: 0.6;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.3;
  }
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--surface-900, #1f2937);
  margin: 0 0 1rem 0;
  letter-spacing: -0.025em;
}

.empty-description {
  color: var(--surface-600, #6b7280);
  font-size: 1rem;
  line-height: 1.6;
  max-width: 28rem;
  margin: 0 0 2rem 0;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.btn-modern.primary {
  background: linear-gradient(135deg, var(--primary-500, #3b82f6) 0%, var(--primary-600, #2563eb) 100%);
  color: white;
  box-shadow: 0 4px 14px 0 rgba(59, 130, 246, 0.4);
}

.btn-modern.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(59, 130, 246, 0.5);
}

.btn-modern.primary:active {
  transform: translateY(0);
}

/* ===== GALLERY CONTAINER ===== */
.gallery-container {
  background: var(--surface-card, white);
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* ===== MODERN GALLERIA OVERRIDES ===== */
:deep(.p-galleria) {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

:deep(.p-galleria-item-container) {
  height: 500px !important;
  max-height: 500px !important;
  background: var(--surface-900, #111827);
  position: relative;
  overflow: hidden;
}

:deep(.p-galleria-thumbnail-container) {
  background: var(--surface-50, #f9fafb);
  padding: 1.5rem;
  border-top: 1px solid var(--surface-200, #e5e7eb);
}

:deep(.p-galleria-thumbnail-items-container) {
  height: 100px !important;
  gap: 0.75rem;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
}

:deep(.p-galleria-thumbnail-item) {
  opacity: 0.7;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border-radius: 0.75rem;
  overflow: hidden;
  padding: 0 !important;
  margin: 0;
  width: 140px !important;
  height: 100px !important;
  min-width: 140px !important;
  min-height: 100px !important;
  max-width: 140px !important;
  max-height: 100px !important;
  position: relative !important;
  display: inline-block !important;
  flex-shrink: 0 !important;
}

:deep(.p-galleria-thumbnail-item > *) {
  width: 100% !important;
  height: 100% !important;
}

:deep(.p-galleria-thumbnail-item:hover) {
  opacity: 1;
  transform: translateY(-2px);
}

:deep(.p-galleria-thumbnail-item.p-galleria-thumbnail-item-current) {
  opacity: 1;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  border: 2px solid var(--primary-500, #3b82f6);
}

/* ===== MODERN IMAGE WRAPPER ===== */
.modern-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: zoom-in;
  background: var(--surface-900, #111827);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container {
  position: relative;
  width: 100%;
  height: 500px;
  max-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0.5rem;
}

.main-image.loading {
  filter: blur(4px) brightness(0.8);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0) 70%,
    rgba(0, 0, 0, 0.3) 100%
  );
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.modern-image-wrapper:hover .image-overlay {
  opacity: 1;
}

/* ===== MODERN LOADING INDICATOR ===== */
.modern-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.loading-pulse {
  position: relative;
  width: 60px;
  height: 60px;
}

.pulse-ring {
  position: absolute;
  border: 3px solid var(--primary-500, #3b82f6);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  opacity: 0.6;
  animation: pulse-ring 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.pulse-ring:nth-child(2) {
  animation-delay: 0.3s;
}

.pulse-ring:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* ===== MODERN CONTROLS ===== */
.modern-controls {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
}

/* Navigation Buttons */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 3.5rem;
  height: 3.5rem;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--surface-700, #374151);
  font-size: 1.25rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  transform: translateY(-50%) scale(0.9);
}

.nav-button.prev {
  left: 1.5rem;
}

.nav-button.next {
  right: 1.5rem;
}

.modern-image-wrapper:hover .nav-button {
  opacity: 1;
  transform: translateY(-50%) scale(1);
}

.nav-button:hover {
  background: var(--primary-500, #3b82f6);
  color: white;
  transform: translateY(-50%) scale(1.05);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}

/* Action Buttons */
.action-buttons {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  gap: 0.75rem;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
}

.modern-image-wrapper:hover .action-buttons {
  opacity: 1;
  transform: translateY(0);
}

.action-btn {
  width: 2.75rem;
  height: 2.75rem;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--surface-600, #6b7280);
  font-size: 1rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.action-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 50%;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-btn.download:hover {
  color: var(--primary-600, #2563eb);
  background: var(--primary-50, #eff6ff);
}

.action-btn.annotate:hover {
  color: var(--green-600, #16a34a);
  background: var(--green-50, #f0fdf4);
}

.action-btn.zoom:hover {
  color: var(--purple-600, #7c3aed);
  background: var(--purple-50, #f5f3ff);
}

.action-btn.delete:hover {
  color: var(--red-600, #dc2626);
  background: var(--red-50, #fef2f2);
}

.action-btn i {
  position: relative;
  z-index: 1;
}

/* Photo Counter */
.photo-counter-modern {
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  padding: 0.5rem 1.25rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  color: var(--surface-700, #374151);
  font-weight: 600;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modern-image-wrapper:hover .photo-counter-modern {
  opacity: 1;
}

.counter-text {
  color: var(--primary-600, #2563eb);
  font-weight: 700;
}

.counter-separator {
  color: var(--surface-400, #9ca3af);
}

.counter-total {
  color: var(--surface-600, #6b7280);
}

/* Photo Info Overlay */
.photo-info-overlay {
  position: absolute;
  bottom: 1.5rem;
  left: 1.5rem;
  right: 1.5rem;
  background: rgba(255, 255, 255, 0.95);
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  max-width: 400px;
}

.modern-image-wrapper:hover .photo-info-overlay {
  opacity: 1;
  transform: translateY(0);
}

.photo-meta {
  margin: 0;
}

.photo-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--surface-900, #1f2937);
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.photo-date {
  font-size: 0.875rem;
  color: var(--surface-600, #6b7280);
  margin: 0;
}

/* ===== MODERN THUMBNAILS ===== */
.modern-thumbnail {
  position: relative;
  width: 140px;
  height: 100px;
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--surface-100, #f3f4f6);
  display: block;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
}

.thumbnail-image,
.thumbnail-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
}

.thumbnail-image.loading {
  filter: blur(2px) brightness(0.8);
}

.video-thumbnail-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  padding: 0;
  margin: 0;
}

.video-play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-600, #2563eb);
  font-size: 1rem;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

.thumbnail-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.modern-thumbnail:hover .thumbnail-overlay {
  opacity: 1;
}

/* ===== HIDE DEFAULT GALLERIA NAVIGATION ===== */
:deep(.p-galleria-item-prev),
:deep(.p-galleria-item-next) {
  display: none !important;
}

/* ===== PAGINATION CONTROLS ===== */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding: 1.5rem;
  background: var(--surface-0, #ffffff);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.pagination-info {
  font-size: 0.95rem;
  color: var(--surface-600, #6b7280);
  font-weight: 500;
}

.pagination-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.pagination-btn {
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--surface-200, #e5e7eb);
  background: var(--surface-0, #ffffff);
  color: var(--surface-700, #374151);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0 0.75rem;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--primary-50, #eff6ff);
  border-color: var(--primary-300, #93c5fd);
  color: var(--primary-600, #2563eb);
  transform: translateY(-1px);
}

.pagination-btn.active {
  background: var(--primary-500, #3b82f6);
  border-color: var(--primary-500, #3b82f6);
  color: white;
  font-weight: 600;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--surface-100, #f3f4f6);
}

.pagination-btn.page-number {
  min-width: 40px;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 1400px) {
  .photo-grid.masonry {
    column-count: 3;
  }
}

@media (max-width: 1024px) {
  .photo-grid.masonry {
    column-count: 2;
  }
  
  .photo-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .gallery-header {
    flex-direction: column;
    gap: 1.5rem;
    align-items: stretch;
  }
  
  .gallery-actions {
    width: 100%;
  }
  
  .btn-modern {
    width: 100%;
    justify-content: center;
  }
  
  .gallery-title {
    font-size: 1.5rem;
  }
  
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  .controls-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-bar {
    max-width: 100%;
    min-width: 0;
  }
  
  .view-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-section {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .pagination-top {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 0 0 0;
  }
  
  .pagination-top .pagination-info {
    font-size: 0.8125rem;
  }
  
  .pagination-top .pagination-btn {
    min-width: 36px;
    height: 36px;
    font-size: 0.875rem;
  }
  
  .sort-dropdown {
    width: 100%;
  }
  
  .photo-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
  
  .photo-grid.masonry {
    column-count: 1;
  }
  
  .photo-grid.list .photo-card {
    flex-direction: column;
  }
  
  .photo-grid.list .photo-card-image {
    width: 100%;
    height: 200px;
  }
    :deep(.p-galleria-item-container) {
    height: 350px !important;
    max-height: 350px !important;
  }
  
  .image-container {
    height: 350px;
    max-height: 350px;
  }
  
  .nav-button {
    width: 3rem;
    height: 3rem;
    font-size: 1rem;
  }
  
  .nav-button.prev {
    left: 1rem;
  }
  
  .nav-button.next {
    right: 1rem;
  }
  
  .action-buttons {
    top: 1rem;
    right: 1rem;
    gap: 0.5rem;
  }
  
  .action-btn {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
  }
  
  .photo-info-overlay {
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    padding: 0.75rem 1rem;
  }
  
  .photo-counter-modern {
    bottom: 1rem;
  }
  
  :deep(.p-galleria-thumbnail-item) {
    width: 100px !important;
    height: 70px !important;
  }
  
  .skeleton-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .pagination-info {
    font-size: 0.875rem;
  }
  
  .pagination-btn {
    min-width: 36px;
    height: 36px;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .gallery-header {
    gap: 1rem;
  }
  
  .gallery-title {
    font-size: 1.25rem;
  }
  
  .upload-area {
    padding: 1rem 1.5rem;
  }
  
  .upload-text {
    font-size: 0.8rem;
  }
  
  .upload-hint {
    font-size: 0.7rem;
  }

  .stats-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .photo-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
    :deep(.p-galleria-item-container) {
    height: 300px !important;
    max-height: 300px !important;
  }
  
  .image-container {
    height: 300px;
    max-height: 300px;
  }
  
  :deep(.p-galleria-thumbnail-container) {
    padding: 1rem;
  }
  
  :deep(.p-galleria-thumbnail-item) {
    width: 80px !important;
    height: 60px !important;
  }
  
  .photo-info-overlay {
    display: none; /* Hide on very small screens for better UX */
  }
}

/* ===== ACCESSIBILITY ===== */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus styles for better accessibility */
.upload-area:focus-visible,
.nav-button:focus-visible,
.action-btn:focus-visible,
.btn-modern:focus-visible {
  outline: 2px solid var(--primary-500, #3b82f6);
  outline-offset: 2px;
}

:deep(.p-galleria-thumbnail-item:focus-visible) {
  outline: 2px solid var(--primary-500, #3b82f6);
  outline-offset: 2px;
}

/* ===== VIDEO MODAL ===== */
.video-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.video-modal-container {
  position: relative;
  width: 100%;
  max-width: 1400px;
  max-height: 90vh;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.video-modal-close {
  position: absolute;
  top: -3rem;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  z-index: 1;
}

.video-modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
  border-color: rgba(255, 255, 255, 0.4);
}

.video-modal-close i {
  font-size: 1.25rem;
}

.video-modal-content {
  background: #000;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.video-modal-player {
  width: 100%;
  max-height: 75vh;
  display: block;
  background: #000;
}

.video-modal-info {
  padding: 1.5rem;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.95));
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.video-modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.5rem 0;
}

.video-modal-date {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

@media (max-width: 768px) {
  .video-modal-overlay {
    padding: 1rem;
  }
  
  .video-modal-close {
    top: -2.5rem;
    width: 2rem;
    height: 2rem;
  }
  
  .video-modal-close i {
    font-size: 1rem;
  }
  
  .video-modal-player {
    max-height: 60vh;
  }
  
  .video-modal-info {
    padding: 1rem;
  }
  
  .video-modal-title {
    font-size: 1rem;
  }
  
  .video-modal-date {
    font-size: 0.75rem;
  }
}

</style>
